import { RoccoLink } from '@rocco/components/link';
import { useLogImpression, useTracker } from '@archipro-website/tracker';
import {
    trackTileMediaDetailClick,
    type TrackTileMediaItemType,
} from '~/modules/tracking/util/trackTileMedia';
import {
    sectionNamesItsOwnEvent,
    tileClickEventForSection,
} from '~/ui-rocco/hooks/tile-click-event-for-section';
import { getSourceSection } from '~/utils/get-source-section';
import { useRef, type MouseEvent, type ReactNode } from 'react';
import { useAtomValue } from 'jotai';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { cn } from '@rocco/utils/cn';
import { uiContextAtomViewTransitionsEnabled } from '@rocco/states/uiContextAtomViewTransitions';
import {
    PRODUCT_HERO_TRANSITION_NAME,
    PROJECT_HERO_TRANSITION_NAME,
    recordTileMorphStart,
    startsPlainNavigation,
    usePrefersReducedMotion,
    useTileHeroViewTransitionStyle,
} from '@rocco/ui/tile-shared/hooks/use-tile-hero-view-transition';
import type { HeroTransitionName } from '@rocco/ui/tile-shared/hooks/use-tile-hero-view-transition';
import { getLogoBadgeBorderColor } from '@rocco/ui/tile-professional/components/logo-badge-border';
import type { PinItemType } from '~/modules/moodboards/type';
import { TileMobileActionsSlot } from '~/modules/moodboards/components';
import { DetailTileAdminMenu } from '~/modules-rocco/admin/components/DetailTileAdminMenu';
import { DetailTileMedia, DETAIL_TILE_MEDIA_CLASS } from './DetailTileMedia';
import { detailTileActionsFor } from './detail-tile-actions';
import type { DetailTile } from './TileCarousel';

/**
 * The snapshot name a tile morphs under, by what it points at. A pin type with
 * no detail hero to morph into (articles, photos, member uploads) maps to
 * nothing and the tile navigates plainly. Professionals do too — the profile
 * page opens on its own intro reel, so a zoomed tile would land under an
 * animation that immediately replaces it.
 */
const heroTransitionNameFor = (
    pinType: PinItemType | undefined,
): HeroTransitionName | undefined => {
    if (pinType === 'ProductPin') return PRODUCT_HERO_TRANSITION_NAME;
    if (pinType === 'ProjectPin') return PROJECT_HERO_TRANSITION_NAME;
    return undefined;
};

/**
 * Which library tile this is, for `TileMediaDetailClick`'s content type. Read
 * from the destination rather than `pinType`, which only tells products from
 * projects and is absent on professional and article tiles.
 */
const tileMediaTypeFor = (link: string): TrackTileMediaItemType | undefined => {
    if (link.includes('/product/')) return 'ProductTile';
    if (link.includes('/project/')) return 'ProjectTile';
    if (link.includes('/professional/')) return 'ProfessionalTile';
    if (link.includes('/article/')) return 'ArticleTile';
    return undefined;
};

/** Map a pin type to the admin item-type the directory admin actions expect. */
const adminItemTypeFor = (
    pinType: PinItemType | undefined,
): 'projects' | 'products' =>
    pinType === 'ProductPin' ? 'products' : 'projects';

export interface DetailTileCardProps {
    tile: DetailTile;
    /** `light` = ink on white. `dark` = light type on a dark section. */
    theme?: 'light' | 'dark';
    hideSubtitle?: boolean;
    /** Aspect/background classes for the media box. */
    mediaClassName?: string;
    /** Classes for the media `img` (e.g. `object-contain` for square studio shots). */
    imgClassName?: string;
    /**
     * The tile's rendered width as a `sizes` string (see `getImagesSizes`), so
     * the image is served through the CDN loader at the size it lands at. Opt-in:
     * without it the source rendition is served untouched, which is what every
     * existing caller does.
     */
    sizes?: string;
    /** Classes for the title/subtitle lines. */
    textClassName?: string;
    /**
     * Classes for the subtitle line alone, applied after `textClassName`. The
     * product card sets its brand line a step below its title, which one shared
     * class cannot express.
     */
    subtitleClassName?: string;
    /** Override the corner logo badge's size / inset (e.g. the larger badge the
     *  professional tile carries in the network + brand-family grids). */
    logoClassName?: string;
    /**
     * A small label over the media's top-left corner — what marks this tile out
     * from the others around it (e.g. "Same range" in the supplier's grid).
     * Sits opposite the Save control, which owns the top-right.
     */
    badge?: ReactNode;
    onTileClick?: (tileId: number) => void;
    /**
     * Below md, set the `•••` in the caption row, level with the title, as
     * the glyph alone rather than a disc over the picture — the treatment the
     * directory grids give their tiles. The title column gives up the width.
     */
    mobileActionsInCaption?: boolean;
    /**
     * Surface this tile sits on; becomes `Source` on its impression and click.
     * Without it a tile in a product page's "same range" row is
     * indistinguishable from one in the directory grid, and `Source` is one of
     * only two keys that survive to the warehouse. Falls back to the coarse
     * path-derived section.
     */
    trackingSource?: string;
    /**
     * Which rail this card sits in, using the same names the Rocco tiles use.
     * A named rail reports its own event instead of `directoryTileClick`, which
     * is how the pre-revamp pages reported it — this card is not a Rocco tile,
     * so it cannot reach the shared tile-actions atom and has to look the name
     * up itself.
     */
    section?: string;
}

/**
 * One detail-page tile — media (with the Save overlay + BM admin menu when the
 * tile opts in) plus title and subtitle lines. The single tile body shared by
 * {@link TileCarousel} and the mobile tile grids.
 */
export const DetailTileCard = ({
    tile,
    theme = 'light',
    hideSubtitle = false,
    mediaClassName,
    imgClassName,
    textClassName,
    subtitleClassName,
    logoClassName,
    badge,
    sizes,
    onTileClick,
    mobileActionsInCaption = false,
    trackingSource,
    section,
}: DetailTileCardProps) => {
    const isDark = theme === 'dark';
    // The caption treatment is a phone layout: desktop keeps its one link
    // around picture and caption, and its hover panel on the picture.
    const { mobile } = useAppDisplayModeContext();
    const actionsInCaption = mobileActionsInCaption && mobile;
    const subtitle = tile.subtitle ?? tile.professional?.name;
    const cardRef = useRef<HTMLDivElement>(null);
    const tracker = useTracker();
    // The revamp grid and the detail-page related rows both render this card
    // instead of the Rocco tiles that carry impressions, so tile impressions
    // went to zero at flag-on. They feed /member/logImpression ->
    // ImpressionsDaily, which is what BM Top Performers and FeaturePlacement
    // delivery counts read — a paid placement billed against this number.
    useLogImpression({
        data: {
            type: 'LibraryItem_Directory',
            itemID: tile.libraryItemId ?? tile.id,
            professionalID: tile.professional?.id ?? 0,
        },
        ref: cardRef,
    });
    // The morph runs wherever a tile does, behind the feature flag alone, so a
    // related row on a detail page zooms into its destination the same way a
    // directory tile does. Every hook runs unconditionally to keep the hook
    // order stable.
    const viewTransitionsFlagOn = useAtomValue(
        uiContextAtomViewTransitionsEnabled,
    );
    const prefersReducedMotion = usePrefersReducedMotion();
    const heroName = heroTransitionNameFor(tile.pinType);
    const morphEnabled =
        viewTransitionsFlagOn &&
        !prefersReducedMotion &&
        heroName !== undefined;
    const heroStyle = useTileHeroViewTransitionStyle(
        morphEnabled ? tile.link : undefined,
        heroName ?? PRODUCT_HERO_TRANSITION_NAME,
    );

    const handleTileClick = (event: MouseEvent<HTMLAnchorElement>) => {
        if (morphEnabled && heroName && startsPlainNavigation(event)) {
            recordTileMorphStart(
                heroName,
                tile.link,
                cardRef.current?.querySelector<HTMLImageElement>(
                    `.${DETAIL_TILE_MEDIA_CLASS} img`,
                )?.currentSrc,
            );
        }
        const source = trackingSource ?? getSourceSection();
        const payload = {
            source,
            itemId: tile.libraryItemId ?? tile.id,
            professionalId: tile.professional?.id ?? 0,
        };
        const namedEvent = tileClickEventForSection(section);
        // A named rail reports its own event and nothing else, the way the
        // switch behind the Rocco tiles does — one event per click, not two.
        // An unnamed one is a browse grid, which is what directoryTileClick is
        // for. No grid position either way: the card does not know its place in
        // the grid, and the legacy CSV ExtraData ("row,column,perRow,position")
        // is better omitted than filled with a guess a parser reads as real.
        if (namedEvent) {
            void tracker.log(namedEvent, {
                url: new URL(window.location.href),
                data: {
                    LibraryItemID: payload.itemId,
                    ProfessionalID: payload.professionalId,
                },
            });
        } else if (!sectionNamesItsOwnEvent(section)) {
            void tracker.log('directoryTileClick', {
                url: new URL(window.location.href),
                data: {
                    LibraryItemID: payload.itemId,
                    ProfessionalID: payload.professionalId,
                    Source: source,
                },
            });
        }
        // The event every other tile on the site reports, and the one the
        // warehouse counts clicks with: it is the click side of
        // `professional_metrics`, which Search Discovery's click share and
        // zero-click queries both read. The revamp's detail rails reported
        // only `directoryTileClick`, so a tile click on a detail page left no
        // mark on either.
        const mediaType = tileMediaTypeFor(tile.link);
        if (mediaType) {
            trackTileMediaDetailClick(tracker, {
                itemID: payload.itemId,
                itemType: mediaType,
                itemName: tile.title ?? '',
            });
        }
        onTileClick?.(tile.id);
    };
    // Clear-cut treatment mirrors rocco TileMedia (the prod tiles): per-image
    // `multiplied` flag → clear-cut fill token + contain + multiply; photos cover.
    // A cut-out on its own off-white backdrop fills with that backdrop instead
    // and skips the blend, so the letterbox carries no seam.
    const isMultiplied = Boolean(tile.images?.[0]?.multiplied);
    const backdrop = isMultiplied ? tile.images?.[0]?.backdrop : undefined;
    const blends = isMultiplied && !backdrop;

    // Action panel config.
    const {
        enquiry: tileEnquiry,
        share: tileShare,
        tileInfo,
        visitWebsite: tileVisitWebsite,
    } = detailTileActionsFor(tile);

    // Title and subtitle, inside the tile's one link on desktop, or their
    // own link in the caption row beside the actions on a phone.
    const caption = (
        <>
            <p
                className={cn(
                    // Figma tile (1679:75719): title Neue Haas 65 Medium,
                    // 22px, leading 1.3; media→text gap 10px (0.625rem).
                    'm-0 font-sans leading-[1.3] font-medium max-md:pt-3 max-md:text-sm md:pt-[0.625rem] md:text-[1.375rem]',
                    isDark ? 'text-white' : 'text-[#181818]',
                    textClassName,
                )}
            >
                {tile.title}
            </p>
            {!hideSubtitle && subtitle ? (
                <p
                    className={cn(
                        // Subtitle is the title ink at 60% — the lightest
                        // step that keeps WCAG AA contrast (4.5:1) on both
                        // themes; the Figma's 40% (1679:75721) fails at 2.5:1.
                        'm-0 font-sans leading-[1.3] max-md:text-sm md:text-[1.375rem]',
                        isDark ? 'text-white/60' : 'text-[#181818]/60',
                        textClassName,
                        subtitleClassName,
                    )}
                >
                    {subtitle}
                </p>
            ) : null}
        </>
    );

    // Mobile actions live outside the tile link so a tap on the `•••` or a
    // sheet-backdrop dismiss can never navigate the tile.
    const mobileActions = (pinType: PinItemType, bare: boolean) => (
        <TileMobileActionsSlot
            pinId={tile.id}
            pinType={pinType}
            libraryItemId={tile.libraryItemId}
            enquiry={tileEnquiry}
            visitWebsite={tileVisitWebsite}
            share={tileShare}
            tileInfo={tileInfo}
            bareTrigger={bare}
            // In 20 from the tile's edge, as the directory grids set it.
            triggerClassName={
                bare
                    ? cn('mr-3', isDark ? 'text-white' : 'text-[#323232]')
                    : undefined
            }
        />
    );

    return (
        <DetailTileAdminMenu
            adminData={tile.adminData}
            itemId={tile.id}
            itemLink={tile.link}
            professionalLink={tile.professionalLink ?? ''}
            itemType={adminItemTypeFor(tile.pinType)}
        >
            <div className="relative" ref={cardRef}>
                <RoccoLink
                    to={tile.link}
                    onClick={handleTileClick}
                    viewTransition={morphEnabled}
                    className="block w-full"
                >
                    <DetailTileMedia
                        style={
                            backdrop
                                ? { ...heroStyle, backgroundColor: backdrop }
                                : heroStyle
                        }
                        src={tile.images?.[0]?.src}
                        alt={tile.images?.[0]?.alt ?? tile.title}
                        sizes={sizes}
                        className={cn(
                            !isMultiplied && 'bg-neutral-200',
                            'max-md:aspect-[291/400] md:aspect-[446/550]',
                            mediaClassName,
                            // Last so a caller's dark section background can't win:
                            // multiply over a dark fill renders the cut-out black.
                            blends && 'bg-[var(--color-clearcut-fill)]',
                        )}
                        imgClassName={cn(
                            'max-w-none',
                            isMultiplied && 'object-contain',
                            blends && 'mix-blend-multiply',
                            imgClassName,
                        )}
                        pinId={tile.pinType ? tile.id : undefined}
                        pinType={tile.pinType}
                        libraryItemId={tile.libraryItemId}
                        enquiry={tileEnquiry}
                        visitWebsite={tileVisitWebsite}
                        share={tileShare}
                        tileInfo={tileInfo}
                    >
                        {/* Above the hover strip, not under it: the strip
                        lays a dark gradient over the top of the tile, and at
                        z-[1] the badge was reading through it. */}
                        {badge ? (
                            <div className="absolute z-20 max-md:top-3 max-md:left-3 md:top-4 md:left-4">
                                {badge}
                            </div>
                        ) : null}
                        {/* Professional logo badge (network tiles). On a phone
                        the disc is 26.2% of the tile's width, the proportion
                        the /professionals directory tile gives its badge, and
                        the logo fills 72% of it, as there. */}
                        {tile.logo ? (
                            <div
                                className={cn(
                                    'absolute flex aspect-square items-center justify-center overflow-hidden rounded-full max-md:top-3 max-md:left-3 max-md:w-[26.2%] md:top-4 md:left-4 md:size-14',
                                    logoClassName,
                                )}
                                style={{
                                    backgroundColor:
                                        tile.logoBackground || '#ffffff',
                                    ...(getLogoBadgeBorderColor(
                                        tile.logoBackground,
                                    )
                                        ? {
                                              border: `1px solid ${getLogoBadgeBorderColor(tile.logoBackground)}`,
                                          }
                                        : {}),
                                }}
                            >
                                <img
                                    src={tile.logo}
                                    alt=""
                                    loading="lazy"
                                    className="size-[72%] object-contain"
                                />
                            </div>
                        ) : null}
                    </DetailTileMedia>
                    {actionsInCaption ? null : caption}
                </RoccoLink>
                {actionsInCaption ? (
                    <div className="flex items-start gap-1">
                        <RoccoLink
                            to={tile.link}
                            onClick={handleTileClick}
                            viewTransition={morphEnabled}
                            className="block min-w-0 flex-1"
                        >
                            {caption}
                        </RoccoLink>
                        {tile.pinType ? (
                            // pt-3 levels the glyph with the title's own
                            // top padding.
                            <div className="shrink-0 pt-3 empty:hidden">
                                {mobileActions(tile.pinType, true)}
                            </div>
                        ) : null}
                    </div>
                ) : tile.pinType ? (
                    mobileActions(tile.pinType, false)
                ) : null}
            </div>
        </DetailTileAdminMenu>
    );
};
